import React, { useState, useEffect, useRef } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Search, X, Gamepad2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GameSearch = ({ selectedGame, onSelectGame, compact = false }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchTimeout = useRef(null);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchGames = async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setIsSearching(true);
    try {
      const response = await axios.get(`${API}/games/search`, { params: { query, limit: 8 } });
      setSearchResults(response.data);
      setShowDropdown(true);
    } catch (error) {
      console.error('Game search failed:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchInput = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    // Debounce search
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => searchGames(query), 300);
  };

  const handleSelectGame = (game) => {
    onSelectGame({
      id: game.id,
      name: game.name,
      cover_url: game.cover_url
    });
    setSearchQuery(game.name);
    setShowDropdown(false);
  };

  const handleClearGame = () => {
    onSelectGame(null);
    setSearchQuery('');
    setSearchResults([]);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {!compact && <Label className="text-gray-400 mb-2 block">Game (optional)</Label>}
      <div className="relative">
        <Search className={`absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 ${compact ? 'w-3 h-3' : 'w-4 h-4'}`} />
        <Input
          placeholder="Search for a game..."
          value={searchQuery}
          onChange={handleSearchInput}
          onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
          className={`pr-10 bg-black/50 border-red-900/30 ${compact ? 'pl-8 h-8 text-sm' : 'pl-10'}`}
          data-testid="game-search-input"
        />
        {(selectedGame || searchQuery) && (
          <button
            onClick={handleClearGame}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-red-400"
            data-testid="clear-game-btn"
          >
            <X className={compact ? 'w-3 h-3' : 'w-4 h-4'} />
          </button>
        )}
      </div>
      
      {/* Selected Game Display */}
      {selectedGame && (
        <div className={`mt-2 flex items-center gap-2 bg-black/30 border border-red-900/20 rounded-lg ${compact ? 'p-1.5' : 'p-2 gap-3'}`} data-testid="selected-game-display">
          {selectedGame.cover_url && (
            <img src={selectedGame.cover_url} alt={selectedGame.name} className={compact ? 'w-6 h-8 object-cover rounded' : 'w-10 h-14 object-cover rounded'} />
          )}
          <div className="flex-1 min-w-0">
            <div className={`font-medium text-white truncate ${compact ? 'text-xs' : 'text-sm'}`}>{selectedGame.name}</div>
            {!compact && <div className="text-xs text-gray-500">ID: {selectedGame.id}</div>}
          </div>
          <Gamepad2 className={`text-green-400 flex-shrink-0 ${compact ? 'w-4 h-4' : 'w-5 h-5'}`} />
        </div>
      )}
      
      {/* Search Results Dropdown */}
      {showDropdown && searchResults.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-black border border-red-900/30 rounded-lg shadow-xl max-h-80 overflow-y-auto" data-testid="game-search-results">
          {searchResults.map((game) => (
            <button
              key={game.id}
              onClick={() => handleSelectGame(game)}
              className="w-full flex items-center gap-3 p-2 hover:bg-red-900/20 transition-colors text-left"
              data-testid={`game-option-${game.id}`}
            >
              {game.cover_url ? (
                <img src={game.cover_url} alt={game.name} className="w-10 h-14 object-cover rounded" />
              ) : (
                <div className="w-10 h-14 bg-gray-800 rounded flex items-center justify-center">
                  <Gamepad2 className="w-5 h-5 text-gray-600" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-white truncate">{game.name}</div>
                {game.summary && (
                  <div className="text-xs text-gray-500 line-clamp-2">{game.summary}</div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
      
      {isSearching && (
        <div className="absolute z-50 w-full mt-1 bg-black border border-red-900/30 rounded-lg p-4 text-center">
          <div className="animate-spin w-5 h-5 border-2 border-red-500 border-t-transparent rounded-full mx-auto"></div>
        </div>
      )}
    </div>
  );
};

export { GameSearch };
export default GameSearch;
